import * as React from 'react';
import styles from './index.less';
import { DatePicker } from 'antd';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import FComponentsLib from '@freelog/components-lib';
import { FI18n, FServiceAPI } from '@freelog/tools-lib';
import FModal from '@/components/FModal';
import FIcons from '@/components/FIcons';
import fMessage from '@/components/fMessage';

export const PODCAST_RSS_EPISODE_LIMIT = 1000;

export interface FPodcastRssEpisodeLimitModalProps {
  open: boolean;
  feedUrl: string;
  importing?: boolean;
  onCancel(): void;
  onStartImport(payload: { pubStartDate: string; pubEndDate: string }): Promise<void>;
}

type QueryResult =
  | { status: 'idle' }
  | { status: 'ok'; count: number }
  | { status: 'fail'; count: number };

function formatApiStartDate(value: Dayjs): string {
  return value.startOf('day').format('YYYY-MM-DD HH:mm:ss');
}

function formatApiEndDate(value: Dayjs): string {
  return value.endOf('day').format('YYYY-MM-DD HH:mm:ss');
}

function FPodcastRssEpisodeLimitModal({
  open,
  feedUrl,
  importing = false,
  onCancel,
  onStartImport,
}: FPodcastRssEpisodeLimitModalProps) {
  const [$dateRange, set$dateRange] = React.useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [$querying, set$querying] = React.useState(false);
  const [$queryResult, set$queryResult] = React.useState<QueryResult>({ status: 'idle' });

  React.useEffect(() => {
    if (open) {
      set$dateRange(null);
      set$querying(false);
      set$queryResult({ status: 'idle' });
    }
  }, [open]);

  const hasDateRange = Boolean($dateRange?.[0] && $dateRange?.[1]);
  const canStartImport = $queryResult.status === 'ok' && !importing && !$querying;

  async function handleQuery() {
    const start = $dateRange?.[0];
    const end = $dateRange?.[1];
    if (!start || !end || !feedUrl) {
      return;
    }

    set$querying(true);
    try {
      const { ret, errCode, msg, data } = await FServiceAPI.Rss.bindingsPreview({
        feedUrl,
        isLoadItemData: 0,
        pubStartDate: formatApiStartDate(start),
        pubEndDate: formatApiEndDate(end),
      });
      if (ret !== 0 || errCode !== 0 || !data) {
        fMessage(msg || '查询失败', 'error');
        set$queryResult({ status: 'idle' });
        return;
      }
      const count = Number(data.matchedItemCount ?? 0);
      if (count > PODCAST_RSS_EPISODE_LIMIT) {
        set$queryResult({ status: 'fail', count });
      } else {
        set$queryResult({ status: 'ok', count });
      }
    } catch {
      fMessage('查询失败', 'error');
      set$queryResult({ status: 'idle' });
    } finally {
      set$querying(false);
    }
  }

  async function handleStartImport() {
    const start = $dateRange?.[0];
    const end = $dateRange?.[1];
    if (!start || !end || $queryResult.status !== 'ok') {
      return;
    }
    await onStartImport({
      pubStartDate: formatApiStartDate(start),
      pubEndDate: formatApiEndDate(end),
    });
  }

  return (
    <FModal
      open={open}
      onCancel={onCancel}
      footer={null}
      closable={false}
      width={760}
      centered
      styles={{ content: { padding: 0 } }}
    >
      <div className={styles.content}>
        <div className={styles.header}>
          <span className={styles.title}>单集数量限制</span>
          <FIcons.FClose className={styles.close} onClick={onCancel} />
        </div>

        <div className={styles.body}>
          <div className={styles.warningBox}>
            <div className={styles.warningInner}>
              {
                // 播客单集数量过多
                FI18n.i18nNext.tAuto('submitpodcastwrss_msg_episodelimited')
              }
            </div>
          </div>

          <div className={styles.dateRow}>
            <div className={styles.dateField}>
              <div className={styles.dateLabel}>
                {
                  // 发布时间筛选
                  FI18n.i18nNext.tAuto('submitpodcastwrss_msg_episodelimited_datepicker')
                }
              </div>
              <div className={styles.datePicker}>
                <DatePicker.RangePicker
                  value={$dateRange}
                  format='YYYY/MM/DD'
                  placeholder={['开始时间', '结束时间']}
                  disabledDate={(current) => current && current > dayjs().endOf('day')}
                  onChange={(value) => {
                    set$dateRange(value);
                    set$queryResult({ status: 'idle' });
                  }}
                />
              </div>
            </div>
            <button
              type='button'
              className={styles.queryBtn}
              disabled={!hasDateRange || $querying || importing}
              onClick={handleQuery}
            >
              {$querying ? '查询中' : '查询'}
            </button>
          </div>

          {$queryResult.status === 'ok' && (
            <div className={[styles.resultRow, styles.resultOk].join(' ')}>
              <FIcons.FCheck style={{ fontSize: 14, color: '#44C28C' }} />
              <span>此日期范围内的单集数量：{$queryResult.count}</span>
            </div>
          )}
          {$queryResult.status === 'fail' && (
            <div className={[styles.resultRow, styles.resultFail].join(' ')}>
              <span className={styles.resultFailLeft}>
                <FIcons.FFail style={{ fontSize: 14, color: '#EE4040' }} />
                <span>此日期范围内的单集数量：{$queryResult.count}</span>
              </span>
              <span>请重新选择</span>
            </div>
          )}
        </div>

        <div className={styles.footer}>
          <FComponentsLib.FRectBtn
            type='transparent'
            className={styles.cancelBtn}
            disabled={importing}
            onClick={onCancel}
          >
            取消
          </FComponentsLib.FRectBtn>
          <FComponentsLib.FRectBtn
            type='primary'
            disabled={!canStartImport}
            loading={importing}
            onClick={handleStartImport}
          >
            开始导入
          </FComponentsLib.FRectBtn>
        </div>
      </div>
    </FModal>
  );
}

export default FPodcastRssEpisodeLimitModal;
